import type { Dispatch, ReactElement, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation, matchPath } from 'react-router-dom';
import { PurchaseOrdersGrid } from '../components/grids/PurchaseOrdersGrid';
import { RequestsGrid } from '../components/grids/RequestsGrid';
import { VendorsGrid } from '../components/grids/VendorsGrid';
import { WellsGrid } from '../components/grids/WellsGrid';
import { RfqGrid } from '../components/grids/RfqGrid';
import { AppHeader } from '../components/layout/AppHeader';
import { UserMenu } from '../components/layout/UserMenu';
import { AssistantDock } from '../components/assistant/AssistantDock';
import { AssistantRegister } from '../components/assistant/AssistantRegister';
import { GlobalSearchBox, ProcurementSearchRegister } from '../components/search/ProcurementSearchRegister';
import { MainNav } from '../components/navigation/MainNav';
import { PurchaseOrderDetailSidePanel } from '../components/panels/PurchaseOrderDetailSidePanel';
import { RequestDetailSidePanel } from '../components/panels/RequestDetailSidePanel';
import { VendorDetailSidePanel } from '../components/panels/VendorDetailSidePanel';
import { WellDetailSidePanel } from '../components/panels/WellDetailSidePanel';
import { RfqWorkflowPanel } from '../components/panels/RfqWorkflowPanel';
import { RequestPoCreateSection } from '../components/panels/RequestPoCreateSection';
import { normalizeRfqStatus, poPrefillFromApprovedRfqQuote } from '../domain/requestForQuote';
import { PurchaseOrderTiles } from '../components/summary/PurchaseOrderTiles';
import { RfqTiles } from '../components/summary/RfqTiles';
import { SummaryTiles } from '../components/summary/SummaryTiles';
import { WellRegionTiles } from '../components/summary/WellRegionTiles';
import { AddWellModal } from '../components/modals/AddWellModal';
import { NewRequestModal } from '../components/modals/NewRequestModal';
import { ModuleHomePage } from './ModuleHomePage';
import { InventoryPage } from './InventoryPage';
import { MaterialTransfersPage } from './MaterialTransfersPage';
import { MaterialTransferFormPage } from './MaterialTransferFormPage';
import { InventoryItemDetailPanel } from '../components/panels/InventoryItemDetailPanel';
import { MaterialTransferSidePanel } from '../components/panels/MaterialTransferSidePanel';
import type {
  LoginResponse,
  PoFormState,
  PoStatusFilter,
  PoTileCounts,
  ProcurementRequest,
  PurchaseOrder,
  RequestCounts,
  RequestFormData,
  StatusFilter,
  DeliveryAddressDirectoryEntry,
  VendorDirectoryEntry,
  WellMaster,
  WellRegionCount,
  RegionMasterDto,
  UpdatePurchaseOrderPayload,
  RequestForQuoteDetail,
  RequestForQuoteSummary,
  RfqStatusFilter,
  RfqTileCounts
} from '../domain/types';
import {
  canPerformProcurementStaffActions,
  canReadAssistant,
  canViewInventoryAndTransfers,
  canViewProcurementModules,
  isReadOnlyRole,
  permissionsOf
} from '../domain/formatters';

export interface AuthenticatedPageProps {
  auth: LoginResponse;
  onLogout: () => void | Promise<void>;
  loading: boolean;
  poRegistryLoading: boolean;
  error: string;
  message: string;
  showRequestsMain: boolean;
  showPurchaseOrdersMain: boolean;
  showWellsMain: boolean;
  showVendorsMain: boolean;
  showRfqMain: boolean;
  statusFilter: StatusFilter;
  setStatusFilter: (f: StatusFilter) => void;
  requestCounts: RequestCounts;
  displayedRequests: ProcurementRequest[];
  requestsPage: number;
  requestsPageSize: number;
  requestsTotalCount: number;
  onRequestsPageChange: (page: number) => void;
  onRequestsPageSizeChange: (pageSize: number) => void;
  requestsSortColumn: string | null;
  requestsSortDirection: 'asc' | 'desc' | null;
  onRequestsSortClick: (columnId: string) => void;
  /** Current request selection; on `/requests/:requestId` matches the URL (see App). */
  selectedId: string | null;
  setSelectedId: (id: string | null) => void;
  /** GET /api/requests/:id failed (not found or forbidden). */
  requestDetailNotFound: boolean;
  purchaseOrdersList: PurchaseOrder[];
  poPage: number;
  poPageSize: number;
  poTotalCount: number;
  onPoPageChange: (page: number) => void;
  onPoPageSizeChange: (pageSize: number) => void;
  selectedPoId: string | null;
  setSelectedPoId: (id: string | null) => void;
  selectedRequest: ProcurementRequest | null;
  selectedPo: PurchaseOrder | null;
  formData: RequestFormData;
  setFormData: Dispatch<SetStateAction<RequestFormData>>;
  draftEditForm: RequestFormData;
  setDraftEditForm: Dispatch<SetStateAction<RequestFormData>>;
  panelComment: string;
  setPanelComment: (v: string) => void;
  onAddRequestNote: (body: string) => void | Promise<void>;
  purchaseOrder: PurchaseOrder | null;
  poForm: PoFormState;
  setPoForm: Dispatch<SetStateAction<PoFormState>>;
  vendors: VendorDirectoryEntry[];
  onReloadVendors: () => void | Promise<void>;
  deliveryAddresses: DeliveryAddressDirectoryEntry[];
  canManageSelectedDraft: boolean;
  poStatusFilter: PoStatusFilter;
  setPoStatusFilter: (f: PoStatusFilter) => void;
  poTileCounts: PoTileCounts;
  rfqStatusFilter: RfqStatusFilter;
  setRfqStatusFilter: (f: RfqStatusFilter) => void;
  rfqTileCounts: RfqTileCounts;
  onSubmitNewRequest: () => void | Promise<void>;
  onSaveDraft: () => void;
  wells: WellMaster[];
  /** Wells directory refresh after add/edit from Wells page. */
  onReloadWells: () => void | Promise<void>;
  wellsPaged: WellMaster[];
  wellsPagedTotal: number;
  wellsPage: number;
  wellsPageSize: number;
  onWellsPageChange: (page: number) => void;
  onWellsPageSizeChange: (pageSize: number) => void;
  wellRegionFilter: string | null;
  onWellRegionFilter: (region: string | null) => void;
  wellRegionCounts: WellRegionCount[];
  wellsPagedLoading: boolean;
  vendorsPaged: VendorDirectoryEntry[];
  vendorsPagedTotal: number;
  vendorsPage: number;
  vendorsPageSize: number;
  onVendorsPageChange: (page: number) => void;
  onVendorsPageSizeChange: (pageSize: number) => void;
  vendorsPagedLoading: boolean;
  authFetch: (url: string, init?: RequestInit) => Promise<Response>;
  newRequestModalOpen: boolean;
  onOpenNewRequest: () => void;
  onCloseNewRequestModal: () => void;
  closePanel: () => void;
  closePoPanel: () => void;
  exportSelectedRequestPdf: () => void;
  exportPurchaseOrderPdfFor: (po: PurchaseOrder) => void;
  updateSelectedDraft: () => void;
  submitSelectedDraftForApproval: () => void;
  deleteSelectedDraft: () => void;
  applyRegionalDecision: (id: string, approve: boolean) => void;
  queueForProcurement: (id: string) => void;
  rejectProcurementRequest: (id: string, comment?: string) => void | Promise<void>;
  createPurchaseOrder: (forProcurementRequestId?: string) => void;
  updatePurchaseOrder: (id: string, payload: UpdatePurchaseOrderPayload) => void | Promise<void>;
  markPoPaid: (id: string) => void;
  emailVendorToPo: (po: PurchaseOrder) => void | Promise<void>;
  markProcured: (id: string) => void;
  markCompleteFinal: (id: string) => void;
  /** GET /api/regions — active region catalog for well/request dropdowns. */
  activeRegions: RegionMasterDto[];
  /** Failed submit-for-validation: new request modal field keys. */
  newRequestSubmitFieldErrors: string[];
  /** Failed submit-for-validation: draft editor field keys. */
  draftSubmitFieldErrors: string[];
  /** Sourcing — request for quote (latest non-cancelled for this procurement request). */
  rfqDetail: RequestForQuoteDetail | null;
  rfqLoading: boolean;
  createRfq: () => void | Promise<void>;
  updateRfq: (
    id: string,
    payload: { requirements: string; incotermsSnapshot: string; vendorIds: string[] }
  ) => void | Promise<void>;
  sendRfqInvitations: (id: string) => void | Promise<void>;
  selectVendorForRfq: (id: string, vendorId: string) => void | Promise<void>;
  cancelRfq: (id: string) => void | Promise<void>;
  canApproveRfqWorkflow: boolean;
  /** POST /api/operations/rfq/{id}/approve */
  approveRfq: (id: string, comment?: string) => void | Promise<void>;
  /** POST /api/operations/rfq/{id}/reject */
  rejectRfq: (id: string, comment?: string) => void | Promise<void>;
  /** RFQ registry tab */
  rfqListRows: RequestForQuoteSummary[];
  rfqListLoading: boolean;
  rfqListPage: number;
  rfqListPageSize: number;
  rfqListTotal: number;
  onRfqListPageChange: (page: number) => void;
  onRfqListPageSizeChange: (pageSize: number) => void;
  selectedRfqRegistryId: string | null;
  setSelectedRfqRegistryId: (id: string | null) => void;
  registryRfqDetail: RequestForQuoteDetail | null;
  registryRfqDetailLoading: boolean;
  poContextRequestForRfq: ProcurementRequest | null;
  /** Loaded when RFQ status is complete (PO created from this quote). */
  rfqRegistryLinkedPo: PurchaseOrder | null;
  onOpenPurchaseOrderFromRfq: (poId: string) => void;
}

export function AuthenticatedPage(props: AuthenticatedPageProps): ReactElement {
  const {
    auth,
    onLogout,
    loading,
    poRegistryLoading,
    error,
    message,
    showRequestsMain,
    showPurchaseOrdersMain,
    showWellsMain,
    showVendorsMain,
    showRfqMain,
    statusFilter,
    setStatusFilter,
    requestCounts,
    displayedRequests,
    requestsPage,
    requestsPageSize,
    requestsTotalCount,
    onRequestsPageChange,
    onRequestsPageSizeChange,
    requestsSortColumn,
    requestsSortDirection,
    onRequestsSortClick,
    selectedId,
    setSelectedId,
    requestDetailNotFound,
    purchaseOrdersList,
    poPage,
    poPageSize,
    poTotalCount,
    onPoPageChange,
    onPoPageSizeChange,
    selectedPoId,
    setSelectedPoId,
    selectedRequest,
    selectedPo,
    formData,
    setFormData,
    draftEditForm,
    setDraftEditForm,
    panelComment,
    setPanelComment,
    onAddRequestNote,
    purchaseOrder,
    poForm,
    setPoForm,
    vendors,
    onReloadVendors,
    deliveryAddresses,
    canManageSelectedDraft,
    poStatusFilter,
    setPoStatusFilter,
    poTileCounts,
    rfqStatusFilter,
    setRfqStatusFilter,
    rfqTileCounts,
    onSubmitNewRequest,
    onSaveDraft,
    wells,
    onReloadWells,
    wellsPaged,
    wellsPagedTotal,
    wellsPage,
    wellsPageSize,
    onWellsPageChange,
    onWellsPageSizeChange,
    wellRegionFilter,
    onWellRegionFilter,
    wellRegionCounts,
    wellsPagedLoading,
    vendorsPaged,
    vendorsPagedTotal,
    vendorsPage,
    vendorsPageSize,
    onVendorsPageChange,
    onVendorsPageSizeChange,
    vendorsPagedLoading,
    authFetch,
    newRequestModalOpen,
    onOpenNewRequest,
    onCloseNewRequestModal,
    closePanel,
    closePoPanel,
    exportSelectedRequestPdf,
    exportPurchaseOrderPdfFor,
    updateSelectedDraft,
    submitSelectedDraftForApproval,
    deleteSelectedDraft,
    applyRegionalDecision,
    queueForProcurement,
    rejectProcurementRequest,
    createPurchaseOrder,
    updatePurchaseOrder,
    markPoPaid,
    emailVendorToPo,
    markProcured,
    markCompleteFinal,
    activeRegions,
    newRequestSubmitFieldErrors,
    draftSubmitFieldErrors,
    rfqDetail,
    rfqLoading,
    createRfq,
    updateRfq,
    sendRfqInvitations,
    selectVendorForRfq,
    cancelRfq,
    canApproveRfqWorkflow,
    approveRfq,
    rejectRfq,
    rfqListRows,
    rfqListLoading,
    rfqListPage,
    rfqListPageSize,
    rfqListTotal,
    onRfqListPageChange,
    onRfqListPageSizeChange,
    selectedRfqRegistryId,
    setSelectedRfqRegistryId,
    registryRfqDetail,
    registryRfqDetailLoading,
    poContextRequestForRfq,
    rfqRegistryLinkedPo,
    onOpenPurchaseOrderFromRfq
  } = props;

  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { requestId: routeRequestId, poId: routePoId, rfqId: routeRfqId } = useParams();
  const materialTransfersEnabled = auth.profile?.materialTransfersEnabled ?? true;
  /** Trailing slash (e.g. /material-transfers/) must count as the list page — strict === hid main content. */
  const isMaterialTransferList = Boolean(
    matchPath({ path: '/material-transfers', end: true }, pathname)
  );
  const isMaterialTransferFormPath = Boolean(
    matchPath({ path: '/material-transfers/new', end: true }, pathname) ||
    matchPath({ path: '/material-transfers/:transferId/edit', end: true }, pathname)
  );
  const showInventoryMain =
    materialTransfersEnabled &&
    Boolean(matchPath({ path: '/inventory', end: true }, pathname)) &&
    canViewInventoryAndTransfers(auth.role);
  const showMaterialTransfersMain =
    materialTransfersEnabled && isMaterialTransferList && canViewInventoryAndTransfers(auth.role);
  const showMaterialTransferForm =
    materialTransfersEnabled && isMaterialTransferFormPath && canViewInventoryAndTransfers(auth.role);
  const isRfqFullPage = Boolean(showRfqMain && routeRfqId);
  const poDetailReadOnly = isReadOnlyRole(auth.role) || !canPerformProcurementStaffActions(auth.role);

  const [selectedWellId, setSelectedWellId] = useState<string | null>(null);
  const [addWellOpen, setAddWellOpen] = useState(false);
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  const [selectedInventoryItemId, setSelectedInventoryItemId] = useState<string | null>(null);
  const [selectedMaterialTransferListId, setSelectedMaterialTransferListId] = useState<string | null>(null);

  /** Mongo well create is retired (410). /wells is a register over masterdata.Well. */
  const canManageWellsAsAdministrator = false;

  const selectedWell = useMemo(() => {
    if (!selectedWellId) {
      return null;
    }
    return (
      wellsPaged.find((w) => w.id === selectedWellId) ??
      wells.find((w) => w.id === selectedWellId) ??
      null
    );
  }, [wellsPaged, wells, selectedWellId]);

  const selectedVendor = useMemo(() => {
    if (!selectedVendorId) {
      return null;
    }
    return (
      vendorsPaged.find((v) => v.id === selectedVendorId) ??
      vendors.find((v) => v.id === selectedVendorId) ??
      null
    );
  }, [vendorsPaged, vendors, selectedVendorId]);

  /** Serialized vendor rows so PO prefill updates when quote amounts change, not when the array reference changes. */
  const registryRfqVendorsJson = JSON.stringify(registryRfqDetail?.vendors ?? []);

  /** Stable PO prefill from awarded vendor quote (avoid resetting on each RFQ poll when data unchanged). */
  /* eslint-disable react-hooks/exhaustive-deps -- deps are content-based; RFQ detail object identity changes every poll */
  const rfqAwardedQuotePrefill = useMemo(() => {
    if (!registryRfqDetail || !poContextRequestForRfq) {
      return null;
    }
    return poPrefillFromApprovedRfqQuote(registryRfqDetail, poContextRequestForRfq);
  }, [
    registryRfqDetail?.id,
    registryRfqDetail?.status,
    registryRfqDetail?.selectedVendorId,
    registryRfqDetail?.incotermsSnapshot,
    poContextRequestForRfq?.id,
    poContextRequestForRfq?.materials?.[0]?.itemId,
    registryRfqVendorsJson
  ]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const showRfqPoCreateSection = useMemo(() => {
    if (!registryRfqDetail || !poContextRequestForRfq || !canPerformProcurementStaffActions(auth.role)) {
      return false;
    }
    const s = normalizeRfqStatus(registryRfqDetail.status);
    if (s === 'complete') {
      return false;
    }
    const prId = registryRfqDetail.procurementRequestId?.trim();
    if (prId && rfqRegistryLinkedPo?.procurementRequestId === prId) {
      return false;
    }
    const rs = poContextRequestForRfq.status;
    if (rs === 'orderPlaced' || rs === 'procured' || rs === 'complete') {
      return false;
    }
    return s === 'approved' || s === 'cancelled';
  }, [registryRfqDetail, poContextRequestForRfq, auth.role, rfqRegistryLinkedPo]);

  useEffect(() => {
    const onInventory = Boolean(matchPath({ path: '/inventory', end: true }, pathname));
    const onMaterialTransfers = Boolean(
      matchPath({ path: '/material-transfers', end: true }, pathname) ||
        matchPath({ path: '/material-transfers/new', end: true }, pathname) ||
        matchPath({ path: '/material-transfers/:transferId/edit', end: true }, pathname)
    );
    if ((onInventory || onMaterialTransfers) && !canViewInventoryAndTransfers(auth.role)) {
      navigate('/', { replace: true });
    }
  }, [pathname, auth.role, navigate]);

  useEffect(() => {
    if (!canViewProcurementModules(auth.role)) {
      if (
        pathname === '/wells' ||
        pathname === '/assistant' ||
        pathname.startsWith('/assistant/') ||
        pathname === '/search' ||
        pathname === '/vendors' ||
        pathname === '/purchase-orders' ||
        pathname.startsWith('/purchase-orders/') ||
        pathname === '/request-for-quotes' ||
        pathname.startsWith('/request-for-quotes/')
      ) {
        navigate('/', { replace: true });
      }
    }
  }, [auth.role, pathname, navigate]);

  useEffect(() => {
    if (pathname === '/' || pathname === '/requests' || pathname.startsWith('/requests/')) {
      setSelectedPoId(null);
    }
    if (pathname === '/purchase-orders' || pathname.startsWith('/purchase-orders/')) {
      setSelectedId(null);
    }
    if (pathname === '/wells' || pathname === '/vendors' || pathname === '/request-for-quotes' || pathname.startsWith('/request-for-quotes/')) {
      setSelectedId(null);
      setSelectedPoId(null);
    }
    if (pathname !== '/wells') {
      setSelectedWellId(null);
      setAddWellOpen(false);
    }
    if (pathname !== '/vendors') {
      setSelectedVendorId(null);
    }
    if (
      Boolean(matchPath({ path: '/inventory', end: true }, pathname)) ||
      isMaterialTransferList ||
      isMaterialTransferFormPath
    ) {
      setSelectedId(null);
      setSelectedPoId(null);
    }
  }, [pathname, isMaterialTransferList, isMaterialTransferFormPath, setSelectedId, setSelectedPoId]);

  useEffect(() => {
    if (routeRequestId) {
      setSelectedId(routeRequestId);
    }
  }, [routeRequestId, setSelectedId]);

  useEffect(() => {
    if (routePoId) {
      setSelectedPoId(routePoId);
    }
  }, [routePoId, setSelectedPoId]);

  useEffect(() => {
    if (!Boolean(matchPath({ path: '/inventory', end: true }, pathname))) {
      setSelectedInventoryItemId(null);
    }
  }, [pathname]);
  useEffect(() => {
    if (!isMaterialTransferList) {
      setSelectedMaterialTransferListId(null);
    }
  }, [isMaterialTransferList]);

  const isRequestFullPage = Boolean(routeRequestId);
  const isPoFullPage = Boolean(routePoId);
  const assistantMatch = matchPath({ path: '/assistant/:conversationId', end: true }, pathname);
  const isAssistantPath = pathname === '/assistant' || Boolean(assistantMatch);
  const canOpenAssistant = canReadAssistant(auth.role, permissionsOf(auth));
  const isAssistantFullPage = Boolean(assistantMatch);
  const isDetailFullPage = isRequestFullPage || isPoFullPage || isRfqFullPage || isAssistantFullPage;
  const showAssistantDock =
    canOpenAssistant &&
    ((showRequestsMain && !isRequestFullPage) || showInventoryMain || showMaterialTransfersMain);
  const [assistantPinnedOpen, setAssistantPinnedOpen] = useState(false);
  const onAssistantPinnedChange = useCallback((pinnedOpen: boolean) => {
    setAssistantPinnedOpen(pinnedOpen);
  }, []);

  useEffect(() => {
    if (!isAssistantPath || canOpenAssistant) {
      return;
    }
    navigate('/', { replace: true });
  }, [isAssistantPath, canOpenAssistant, navigate]);

  function backFromRequestPage(): void {
    navigate('/requests');
    setSelectedId(null);
  }

  function backFromPoPage(): void {
    navigate('/purchase-orders');
    setSelectedPoId(null);
  }

  function backFromRfqPage(): void {
    navigate('/request-for-quotes');
    setSelectedRfqRegistryId(null);
  }

  function closeRfqPanel(): void {
    setSelectedRfqRegistryId(null);
    if (pathname.startsWith('/request-for-quotes/')) {
      navigate('/request-for-quotes');
    }
  }

  const requestPanelRest = {
    auth,
    canManageSelectedDraft,
    draftEditForm,
    setDraftEditForm,
    wells,
    activeRegions,
    authFetch,
    panelComment,
    setPanelComment,
    onAddRequestNote,
    purchaseOrder,
    poForm,
    setPoForm,
    createPurchaseOrder,
    vendors,
    onReloadVendors,
    deliveryAddresses,
    onExportRequestPdf: exportSelectedRequestPdf,
    onExportPoPdf: exportPurchaseOrderPdfFor,
    onUpdateDraft: updateSelectedDraft,
    onSubmitDraftForApproval: submitSelectedDraftForApproval,
    onDeleteDraft: deleteSelectedDraft,
    onRegionalDecision: applyRegionalDecision,
    onQueueForProcurement: queueForProcurement,
    onRejectProcurement: rejectProcurementRequest,
    onUpdatePurchaseOrder: updatePurchaseOrder,
    onMarkPoPaid: markPoPaid,
    onMarkProcured: markProcured,
    onMarkComplete: markCompleteFinal,
    draftSubmitFieldErrors,
    rfqDetail,
    rfqLoading,
    onCreateRfq: createRfq,
    onUpdateRfq: updateRfq,
    onSendRfqInvitations: sendRfqInvitations,
    onSelectVendorForRfq: selectVendorForRfq,
    onCancelRfq: cancelRfq,
    canApproveRfqWorkflow,
    onApproveRfq: approveRfq,
    onRejectRfq: rejectRfq,
    onOpenRfqPage: (id: string) => navigate(`/request-for-quotes/${encodeURIComponent(id)}`),
    onOpenProcurementRequest: (id: string) => navigate(`/requests/${encodeURIComponent(id)}`),
    sourcingEnabled: auth.profile?.sourcingEnabled ?? true
  };

  return (
    <div className={`page page-authed${isDetailFullPage ? ' page-authed--detail-full' : ''}`}>
      <AppHeader
        navSlot={
          pathname === '/' ? undefined : (
            <MainNav
              role={auth.role}
              sourcingEnabled={auth.profile?.sourcingEnabled ?? true}
              materialTransfersEnabled={auth.profile?.materialTransfersEnabled ?? true}
              permissions={permissionsOf(auth)}
            />
          )
        }
        endSlot={
          <>
            {pathname !== '/' && canViewProcurementModules(auth.role) && <GlobalSearchBox />}
            <UserMenu auth={auth} onLogout={onLogout} />
          </>
        }
      />
      <div
        className={`app-content${isDetailFullPage ? ' app-content--detail-full' : ''}${
          showAssistantDock && assistantPinnedOpen ? ' app-content--assistant-docked' : ''
        }`}
      >
        <div className="assistant-dock-main">
        {pathname === '/' && <ModuleHomePage auth={auth} />}
        {loading && showRequestsMain && !routeRequestId && <p>Loading requests...</p>}
        {poRegistryLoading && showPurchaseOrdersMain && !routePoId && <p>Loading purchase orders...</p>}
        {error && <p className="error">{error}</p>}
        {message && <p className="message">{message}</p>}

        {showInventoryMain && (
          <div
            className={`requests-shell${selectedInventoryItemId ? ' has-panel' : ''}`}
            style={{ minWidth: 0 }}
          >
            <InventoryPage
              authFetch={authFetch}
              selectedInventoryItemId={selectedInventoryItemId}
              onSelectInventoryItem={setSelectedInventoryItemId}
            />
            {selectedInventoryItemId && (
              <InventoryItemDetailPanel
                inventoryId={selectedInventoryItemId}
                onClose={() => setSelectedInventoryItemId(null)}
                authFetch={authFetch}
                onOpenMaterialTransfer={(id) => {
                  setSelectedInventoryItemId(null);
                  void navigate(`/material-transfers/${encodeURIComponent(id)}/edit`);
                }}
              />
            )}
          </div>
        )}
        {showMaterialTransfersMain && (
          <div
            className={`requests-shell${selectedMaterialTransferListId ? ' has-panel' : ''}`}
            style={{ minWidth: 0 }}
          >
            <MaterialTransfersPage
              authFetch={authFetch}
              selectedMaterialTransferId={selectedMaterialTransferListId}
              onSelectMaterialTransfer={setSelectedMaterialTransferListId}
              onOpenTransferForEdit={(id) =>
                void navigate(`/material-transfers/${encodeURIComponent(id)}/edit`)
              }
            />
            {selectedMaterialTransferListId && (
              <MaterialTransferSidePanel
                transferId={selectedMaterialTransferListId}
                onClose={() => setSelectedMaterialTransferListId(null)}
                authFetch={authFetch}
                onOpenFullForm={(id) =>
                  void navigate(`/material-transfers/${encodeURIComponent(id)}/edit`)
                }
              />
            )}
          </div>
        )}
        {showMaterialTransferForm && (
          <MaterialTransferFormPage
            auth={auth}
            authFetch={authFetch}
            activeRegions={activeRegions}
            mode={matchPath({ path: '/material-transfers/new', end: true }, pathname) ? 'new' : 'edit'}
          />
        )}

        {showPurchaseOrdersMain && !isPoFullPage && (
          <PurchaseOrderTiles
            counts={poTileCounts}
            statusFilter={poStatusFilter}
            onStatusFilter={setPoStatusFilter}
          />
        )}
        {showRfqMain && !isRfqFullPage && (
          <RfqTiles
            role={auth.role}
            counts={rfqTileCounts}
            statusFilter={rfqStatusFilter}
            onStatusFilter={setRfqStatusFilter}
          />
        )}
        {showRequestsMain && !isRequestFullPage && (
          <SummaryTiles role={auth.role} counts={requestCounts} statusFilter={statusFilter} onStatusFilter={setStatusFilter} />
        )}

        {showRfqMain &&
          (isRfqFullPage ? (
            registryRfqDetail ? (
              <div className="detail-page-shell">
                <aside className="side-panel side-panel--page" aria-label="Request for quote details">
                  <div className="side-panel-header">
                    <div className="side-panel-title-row">
                      <button type="button" className="secondary panel-back-btn" onClick={backFromRfqPage}>
                        ← Back to list
                      </button>
                      <h3>{registryRfqDetail.rfqNumber}</h3>
                    </div>
                  </div>
                  <div className="side-panel-body">
                    <div className="panel-actions">
                      <RfqWorkflowPanel
                        rfqDetail={registryRfqDetail}
                        rfqLoading={registryRfqDetailLoading}
                        vendors={vendors}
                        activeRegions={activeRegions}
                        onCreateRfq={createRfq}
                        onUpdateRfq={updateRfq}
                        onSendRfqInvitations={sendRfqInvitations}
                        onSelectVendorForRfq={selectVendorForRfq}
                        onCancelRfq={cancelRfq}
                        canApproveRfqWorkflow={canApproveRfqWorkflow}
                        onApproveRfq={approveRfq}
                        onRejectRfq={rejectRfq}
                        onOpenProcurementRequest={(id) => navigate(`/requests/${encodeURIComponent(id)}`)}
                        readOnly={isReadOnlyRole(auth.role)}
                        allowCreate={false}
                        showSectionHeading={false}
                        onReloadVendors={onReloadVendors}
                        authFetch={authFetch}
                      />
                    </div>
                    {showRfqPoCreateSection && poContextRequestForRfq && (
                        <RequestPoCreateSection
                          selectedRequest={poContextRequestForRfq}
                          poForm={poForm}
                          setPoForm={setPoForm}
                          vendors={vendors}
                          deliveryAddresses={deliveryAddresses}
                          onCreatePurchaseOrder={() => void createPurchaseOrder(poContextRequestForRfq.id)}
                          onReloadVendors={onReloadVendors}
                          authFetch={authFetch}
                          rfqSelectedVendorId={
                            normalizeRfqStatus(registryRfqDetail.status) === 'approved'
                              ? registryRfqDetail.selectedVendorId ?? null
                              : null
                          }
                          rfqDefaultIncoterms={
                            normalizeRfqStatus(registryRfqDetail.status) === 'approved'
                              ? registryRfqDetail.incotermsSnapshot ?? null
                              : null
                          }
                          rfqAwardedQuote={rfqAwardedQuotePrefill}
                        />
                      )}
                    {rfqRegistryLinkedPo ? (
                      <div className="panel-actions po-linked-from-rfq">
                        <h4>Purchase order</h4>
                        <PurchaseOrderDetailSidePanel
                          po={rfqRegistryLinkedPo}
                          variant="embedded"
                          readOnly
                          onClose={() => {}}
                          onExportPdf={exportPurchaseOrderPdfFor}
                          onMarkPaid={() => {}}
                          onOpenPoFullPage={onOpenPurchaseOrderFromRfq}
                        />
                      </div>
                    ) : null}
                  </div>
                </aside>
              </div>
            ) : registryRfqDetailLoading ? (
              <p className="hint detail-page-loading">Loading quote request…</p>
            ) : (
              <p className="hint detail-page-loading">Quote request not found.</p>
            )
          ) : (
            <div className={`requests-shell rfq-registry-shell ${selectedRfqRegistryId ? 'has-panel' : ''}`}>
              <RfqGrid
                rows={rfqListRows}
                selectedRfqId={selectedRfqRegistryId}
                onSelectRfq={(id) => setSelectedRfqRegistryId(id)}
                onOpenRfqInPage={(id) => navigate(`/request-for-quotes/${encodeURIComponent(id)}`)}
                loading={rfqListLoading}
                page={rfqListPage}
                pageSize={rfqListPageSize}
                totalCount={rfqListTotal}
                onPageChange={onRfqListPageChange}
                onPageSizeChange={onRfqListPageSizeChange}
              />
              {selectedRfqRegistryId && (
                <aside className="side-panel" aria-label="Request for quote details">
                  <div className="side-panel-header">
                    <h3>{registryRfqDetail?.rfqNumber ?? 'Quote request'}</h3>
                    <div className="side-panel-actions">
                      <button type="button" className="icon-close" onClick={closeRfqPanel} aria-label="Close panel">
                        ×
                      </button>
                    </div>
                  </div>
                  <div className="side-panel-body">
                    {registryRfqDetailLoading && !registryRfqDetail ? (
                      <p className="hint">Loading…</p>
                    ) : registryRfqDetail ? (
                      <>
                        <RfqWorkflowPanel
                          rfqDetail={registryRfqDetail}
                          rfqLoading={false}
                          vendors={vendors}
                          activeRegions={activeRegions}
                          onCreateRfq={createRfq}
                          onUpdateRfq={updateRfq}
                          onSendRfqInvitations={sendRfqInvitations}
                          onSelectVendorForRfq={selectVendorForRfq}
                          onCancelRfq={cancelRfq}
                          canApproveRfqWorkflow={canApproveRfqWorkflow}
                          onApproveRfq={approveRfq}
                          onRejectRfq={rejectRfq}
                          onOpenFullPage={(id) => navigate(`/request-for-quotes/${encodeURIComponent(id)}`)}
                          onOpenProcurementRequest={(id) => navigate(`/requests/${encodeURIComponent(id)}`)}
                          readOnly={isReadOnlyRole(auth.role)}
                          allowCreate={false}
                          showSectionHeading
                          onReloadVendors={onReloadVendors}
                          authFetch={authFetch}
                        />
                        {showRfqPoCreateSection && poContextRequestForRfq && (
                            <RequestPoCreateSection
                              selectedRequest={poContextRequestForRfq}
                              poForm={poForm}
                              setPoForm={setPoForm}
                              vendors={vendors}
                              deliveryAddresses={deliveryAddresses}
                              onCreatePurchaseOrder={() => void createPurchaseOrder(poContextRequestForRfq.id)}
                              onReloadVendors={onReloadVendors}
                              authFetch={authFetch}
                              rfqSelectedVendorId={
                                normalizeRfqStatus(registryRfqDetail.status) === 'approved'
                                  ? registryRfqDetail.selectedVendorId ?? null
                                  : null
                              }
                              rfqDefaultIncoterms={
                                normalizeRfqStatus(registryRfqDetail.status) === 'approved'
                                  ? registryRfqDetail.incotermsSnapshot ?? null
                                  : null
                              }
                              rfqAwardedQuote={rfqAwardedQuotePrefill}
                            />
                          )}
                        {rfqRegistryLinkedPo ? (
                          <div className="panel-actions po-linked-from-rfq">
                            <h4>Purchase order</h4>
                            <PurchaseOrderDetailSidePanel
                              po={rfqRegistryLinkedPo}
                              variant="embedded"
                              readOnly
                              onClose={() => {}}
                              onExportPdf={exportPurchaseOrderPdfFor}
                              onMarkPaid={() => {}}
                              onOpenPoFullPage={onOpenPurchaseOrderFromRfq}
                            />
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <p className="hint">Could not load this quote request.</p>
                    )}
                  </div>
                </aside>
              )}
            </div>
          ))}

        {showRequestsMain &&
          (isRequestFullPage ? (
            selectedRequest ? (
              <div className="detail-page-shell">
                <RequestDetailSidePanel
                  {...requestPanelRest}
                  selectedRequest={selectedRequest}
                  variant="page"
                  onClose={backFromRequestPage}
                  onBack={backFromRequestPage}
                  readOnly={isReadOnlyRole(auth.role)}
                />
              </div>
            ) : requestDetailNotFound ? (
              <div className="detail-page-shell">
                <p className="hint detail-page-loading">
                  This request could not be loaded. It may not exist or you may not have access to it in your regions.
                </p>
                <button type="button" className="secondary" onClick={backFromRequestPage}>
                  Back to requests
                </button>
              </div>
            ) : (
              <p className="hint detail-page-loading">Loading request…</p>
            )
          ) : (
            <div className={`requests-shell ${selectedRequest ? 'has-panel' : ''}`}>
              <RequestsGrid
                role={auth.role}
                rows={displayedRequests}
                selectedId={selectedId}
                onSelectRequest={(id) => setSelectedId(id)}
                onOpenRequestInPage={(id) => navigate(`/requests/${id}`)}
                statusFilter={statusFilter}
                onStatusFilterChange={setStatusFilter}
                page={requestsPage}
                pageSize={requestsPageSize}
                totalCount={requestsTotalCount}
                onPageChange={onRequestsPageChange}
                onPageSizeChange={onRequestsPageSizeChange}
                onNewRequest={auth.role === 'Requestor' ? onOpenNewRequest : undefined}
                deliveryAddresses={deliveryAddresses}
                sortColumn={requestsSortColumn}
                sortDirection={requestsSortDirection}
                onSortClick={onRequestsSortClick}
              />
              {selectedRequest && (
                <RequestDetailSidePanel
                  {...requestPanelRest}
                  selectedRequest={selectedRequest}
                  variant="panel"
                  onClose={closePanel}
                  readOnly={isReadOnlyRole(auth.role)}
                />
              )}
            </div>
          ))}

        {showPurchaseOrdersMain &&
          (isPoFullPage ? (
            selectedPo ? (
              <div className="detail-page-shell">
                <PurchaseOrderDetailSidePanel
                  po={selectedPo}
                  variant="page"
                  onClose={backFromPoPage}
                  onBack={backFromPoPage}
                  onExportPdf={exportPurchaseOrderPdfFor}
                  onUpdatePurchaseOrder={updatePurchaseOrder}
                  onMarkPaid={markPoPaid}
                  onEmailVendor={poDetailReadOnly ? undefined : emailVendorToPo}
                  readOnly={poDetailReadOnly}
                />
              </div>
            ) : (
              <p className="hint detail-page-loading">Loading purchase order…</p>
            )
          ) : (
            <div className={`requests-shell po-registry-shell ${selectedPo ? 'has-panel' : ''}`}>
              <PurchaseOrdersGrid
                rows={purchaseOrdersList}
                selectedPoId={selectedPoId}
                onSelectPo={(id) => setSelectedPoId(id)}
                onOpenPoInPage={(id) => navigate(`/purchase-orders/${id}`)}
                loading={poRegistryLoading}
                page={poPage}
                pageSize={poPageSize}
                totalCount={poTotalCount}
                onPageChange={onPoPageChange}
                onPageSizeChange={onPoPageSizeChange}
                statusFilter={poStatusFilter}
                onStatusFilterChange={setPoStatusFilter}
              />
              {selectedPo && (
                <PurchaseOrderDetailSidePanel
                  po={selectedPo}
                  variant="panel"
                  onClose={closePoPanel}
                  onExportPdf={exportPurchaseOrderPdfFor}
                  onUpdatePurchaseOrder={updatePurchaseOrder}
                  onMarkPaid={markPoPaid}
                  onEmailVendor={poDetailReadOnly ? undefined : emailVendorToPo}
                  readOnly={poDetailReadOnly}
                />
              )}
            </div>
          ))}

        {showWellsMain && (
          <>
            <AddWellModal
              open={addWellOpen}
              onClose={() => setAddWellOpen(false)}
              authFetch={authFetch}
              activeRegions={activeRegions}
              onWellCreated={async (w) => {
                await onReloadWells();
                setSelectedWellId(w.id);
              }}
            />
            <WellRegionTiles
              regions={wellRegionCounts}
              selectedRegion={wellRegionFilter}
              onSelectRegion={onWellRegionFilter}
            />
            <div className={`requests-shell wells-registry-shell ${selectedWell ? 'has-panel' : ''}`}>
              <WellsGrid
                rows={wellsPaged}
                selectedWellId={selectedWellId}
                onSelectWell={setSelectedWellId}
                activeRegions={activeRegions}
                canAddWell={canManageWellsAsAdministrator}
                onAddWell={() => setAddWellOpen(true)}
                loading={wellsPagedLoading}
                page={wellsPage}
                pageSize={wellsPageSize}
                totalCount={wellsPagedTotal}
                onPageChange={onWellsPageChange}
                onPageSizeChange={onWellsPageSizeChange}
              />
              {selectedWell && (
                <WellDetailSidePanel
                  well={selectedWell}
                  activeRegions={activeRegions}
                  onClose={() => setSelectedWellId(null)}
                  authFetch={authFetch}
                  onWellUpdated={() => void onReloadWells()}
                  canEditWell={canManageWellsAsAdministrator}
                />
              )}
            </div>
          </>
        )}
        {pathname === '/search' && canViewProcurementModules(auth.role) && (
          <ProcurementSearchRegister authFetch={authFetch} />
        )}
        {isAssistantPath && canOpenAssistant && <AssistantRegister authFetch={authFetch} />}
        {isAssistantPath && !canOpenAssistant && (
          <p className="error">You do not have permission to open Assistant.</p>
        )}
        {showVendorsMain && (
          <div className={`requests-shell vendors-registry-shell ${selectedVendor ? 'has-panel' : ''}`}>
            <VendorsGrid
              rows={vendorsPaged}
              selectedVendorId={selectedVendorId}
              onSelectVendor={setSelectedVendorId}
              loading={vendorsPagedLoading}
              page={vendorsPage}
              pageSize={vendorsPageSize}
              totalCount={vendorsPagedTotal}
              onPageChange={onVendorsPageChange}
              onPageSizeChange={onVendorsPageSizeChange}
            />
            {selectedVendor && (
              <VendorDetailSidePanel vendor={selectedVendor} onClose={() => setSelectedVendorId(null)} />
            )}
          </div>
        )}

        </div>

        {showAssistantDock && (
          <AssistantDock authFetch={authFetch} onPinnedChange={onAssistantPinnedChange} />
        )}

        {auth.role === 'Requestor' && (
          <NewRequestModal
            open={newRequestModalOpen}
            onClose={onCloseNewRequestModal}
            formData={formData}
            setFormData={setFormData}
            wells={wells}
            activeRegions={activeRegions}
            deliveryAddresses={deliveryAddresses}
            submitFieldErrorKeys={newRequestSubmitFieldErrors}
            onSubmit={onSubmitNewRequest}
            onSaveDraft={onSaveDraft}
            authFetch={authFetch}
          />
        )}
      </div>
    </div>
  );
}
